import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/common";
import { ScanLine, FileText, Truck, MapPin, ChevronRight, RefreshCcw, Wifi, WifiOff, DownloadCloud, Trash2 } from "lucide-react";
import QrDisplay from "@/components/QrDisplay";
import { decaPdfPath, descargaUrl } from "@/lib/publicUrls";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { syncConductorDecas, getCachedDecas, getCachedPdfBlob, getLastSync, clearOfflineCache } from "@/lib/offline";
import { toast } from "sonner";
import { fmtFechaHora } from "@/lib/fechas";
import { fmtEnvios, fmtNumeroDeca } from "@/lib/textos";

export default function ConductorPage() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [online, setOnline] = useState(navigator.onLine);
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadCached = async () => {
    const cached = await getCachedDecas();
    setItems(cached);
    setLastSync(await getLastSync());
  };

  const sync = async () => {
    setLoading(true);
    try {
      await syncConductorDecas();
      await loadCached();
      toast.success("Sincronizado offline");
    } catch (e) {
      toast.error("No se pudo sincronizar. Trabajando con datos en caché.");
      await loadCached();
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    (async () => {
      await loadCached();
      if (!navigator.onLine) return;
      try {
        await syncConductorDecas();
        await loadCached();
      } catch (_e) {
        /* sin red o API caída: nos quedamos con la caché */
      }
    })();
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []); // eslint-disable-line

  const clearAll = async () => {
    await clearOfflineCache();
    setItems([]); setLastSync(null);
    toast.info("Caché offline vaciada");
  };

  return (
    <div>
      <PageHeader
        title="Modo conductor"
        subtitle="Tus DeCA de ruta. Muestra el QR a pantalla completa para inspección. Funciona sin conexión tras la primera sincronización."
        testid="page-conductor"
        actions={<>
          <Button variant="outline" onClick={sync} disabled={loading || !online} data-testid="btn-sync">
            <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? "animate-spin":""}`}/> Sincronizar
          </Button>
          <Button variant="outline" onClick={clearAll} data-testid="btn-clear-cache">
            <Trash2 className="h-4 w-4 mr-2"/> Vaciar caché
          </Button>
        </>}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className={`status-badge ${online ? "status-generado" : "status-modificado"}`} data-testid="offline-status">
          {online ? <Wifi className="h-3 w-3"/> : <WifiOff className="h-3 w-3"/>} {online ? "Online" : "Offline"}
        </span>
        {lastSync ? <span className="text-xs text-slate-500">Última sinc.: {fmtFechaHora(lastSync)}</span> : <span className="text-xs text-slate-500">Sin sincronizar todavía</span>}
        <span className="text-xs text-slate-500 ml-2 flex items-center gap-1"><DownloadCloud className="h-3 w-3"/> {items.length} DeCA en caché</span>
      </div>

      {loading && items.length === 0 ? (
        <Card className="p-8 text-center">
          <RefreshCcw className="h-8 w-8 mx-auto text-slate-400 animate-spin"/>
          <div className="mt-2 font-medium">Descargando DeCA y PDFs para uso offline…</div>
          <div className="text-sm text-slate-500">Puede tardar unos segundos si es la primera vez.</div>
        </Card>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center">
          <ScanLine className="h-8 w-8 mx-auto text-slate-400"/>
          <div className="mt-2 font-medium">No tienes DeCA en caché</div>
          <div className="text-sm text-slate-500">Pulsa <b>Sincronizar</b> mientras tienes conexión. Solo se muestran DeCA con descarga pública activa.</div>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((d) => {
            const publicUrl = descargaUrl(d.download_token);
            return (
              <Card key={d.id} className="p-4" data-testid={`conductor-card-${d.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="label-tag">DeCA</div>
                    <div className="text-lg font-semibold text-slate-900 truncate" style={{ fontFamily: "Chivo" }}>{d.transportista_snapshot.razon_social}</div>
                    <div className="text-xs text-slate-500 font-mono">{fmtNumeroDeca(d)} · v{d.version}</div>
                  </div>
                  <StatusBadge estado={d.estado}/>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-1.5 text-slate-600"><Truck className="h-4 w-4"/> {(d.matriculas||[]).join(", ") || "—"}</div>
                  <div className="flex items-center gap-1.5 text-slate-600"><MapPin className="h-4 w-4"/> {fmtEnvios(d.num_envios)}</div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Button variant="outline" className="w-full tap-44" data-testid={`btn-pdf-offline-${d.id}`} onClick={async () => {
                    const blob = await getCachedPdfBlob(d.id);
                    if (blob) {
                      const url = URL.createObjectURL(blob);
                      window.open(url, "_blank");
                    } else if (online) {
                      window.open(decaPdfPath(d.id), "_blank", "noopener,noreferrer");
                    } else {
                      toast.error("PDF no disponible offline. Sincroniza antes.");
                    }
                  }}><FileText className="h-4 w-4 mr-2"/> PDF</Button>
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button className="bg-blue-700 hover:bg-blue-800 text-white w-full tap-44" data-testid={`btn-qr-${d.id}`}><ScanLine className="h-4 w-4 mr-2"/> Mostrar QR</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader><DialogTitle>{fmtNumeroDeca(d)}</DialogTitle></DialogHeader>
                      <div className="flex flex-col items-center gap-3 py-3">
                        <QrDisplay value={publicUrl} size={320}/>
                        <div className="text-xs text-slate-500 text-center max-w-xs">
                          Muestra este QR al inspector. La URL descarga el PDF directamente, sin autenticación.
                        </div>
                        <Button variant="outline" onClick={()=>nav(`/decas/${d.id}`)}>Ver detalle <ChevronRight className="h-4 w-4 ml-1"/></Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
